<template>
  <Teleport :to="props.teleportTarget || 'body'">
    <div v-if="visible" class="modal-backdrop" @click.self="onClose">
      <div ref="modalEl" class="modal" :class="{ 'is-docked': isDocked }" :style="modalStyle"
        @keydown.capture="handleModalKeydown">
        <button type="button" class="dock-handle" @pointerdown.stop @click.stop="toggleDock"
          :aria-label="isDocked ? 'Mostrar' : 'Recolher'" v-html="isDocked ? chevronLeftSvg : chevronRightSvg" />

        <h3>{{ layout?.labels.title }}</h3>

        <img :src="closeIcon" class="close-icon" @pointerdown.stop @click.stop="onClose" />

        <div v-if="showLatexMode" class="mode-toggle">
          <label>
            <span>{{ layout?.labels.latex }}</span>
            <input type="checkbox" v-model="latexMode" />
            <span class="toggle-switch">
              <span class="toggle-knob"></span>
            </span>
          </label>
        </div>

        <div class="field-label">{{ layout?.labels.formula }}</div>

        <template v-if="!latexMode">
          <math-field ref="mathFieldEl" @input="syncFromMathField"
            @pointerdown.capture="prepareMathFieldForVirtualKeyboard" @focus="prepareMathFieldForVirtualKeyboard"
            class="mathlive-field" :class="{
              'hide-menu-toggle': !showMenuToggle,
              'hide-vk-toggle': !showVirtualKeyboardToggle
            }" math-mode-space="\;"></math-field>

          <div class="modal-buttons">
            <button type="button" class="btn-secondary" @click="onClose">
              {{ layout?.labels.cancel }}
            </button>
            <button type="button" class="btn-primary" @click="onConfirm">
              {{ layout?.labels.insert }}
            </button>
          </div>

          <div class="operators">
            <div v-for="(group, gIdx) in operatorsS" :key="gIdx" class="operators-group">
              <button v-for="(op, oIdx) in group" :key="oIdx" type="button" class="op-btn"
                @click="insertOperator(op[1])">
                <span v-html="renderOp(op[0])"></span>
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <textarea ref="textareaRef" v-model="latexValue" class="latex-textarea"
            :placeholder="layout?.helps.formula"></textarea>

          <div class="preview-label">{{ layout?.labels.preview }}</div>
          <div class="latex-preview" v-html="latexHtml"></div>
          <p v-if="latexError" class="latex-error">{{ latexError }}</p>

          <div class="modal-buttons">
            <button type="button" class="btn-secondary" @click="onClose">
              {{ layout?.labels.cancel }}
            </button>
            <button type="button" class="btn-primary" @click="onConfirm">
              {{ layout?.labels.insert }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, ref, nextTick, onMounted, onBeforeUnmount, toRefs } from "vue";
import katex from "katex";
import "katex/dist/katex.min.css";
import "mathlive";
import { getComputedPageLayout, getIcon, IPageLayout } from "@/helpers/api";
import { useDeviceStatus } from "@/stores/deviceStatus";

type Operator = [string, string];
type OperatorGroup = Operator[];
type OperatorsMatrix = OperatorGroup[];

interface _1015 extends IPageLayout {
  labels: {
    latex: string;
    title: string;
    cancel: string;
    insert: string;
    formula: string;
    preview: string;
  };
  helps: {
    formula: string;
  };
  commands: {
    latex?: OperatorsMatrix;
  };
}

const layout = getComputedPageLayout<_1015>("1015");

const deviceStatus = useDeviceStatus();
const { platform } = toRefs(deviceStatus);

const closeIcon = ref<string>();

onMounted(async () => {
  closeIcon.value = await getIcon("1010", "close");
});

const props = defineProps<{
  modelValue: string;
  visible: boolean;
  operators?: OperatorsMatrix | null;
  fromModal?: boolean;
  teleportTarget?: HTMLElement | null;
  mathLiveOptions?: {
    showLatexMode?: boolean;
    showMenuToggle?: boolean;
    showVirtualKeyboard?: boolean;
    keyboardLayouts?: string[];
  } | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "close"): void;
  (e: "confirm", value: string): void;
}>();

const latexValue = computed({
  get: () => props.modelValue,
  set: (val: string) => emit("update:modelValue", val),
});

const operatorsS = computed<OperatorsMatrix>(() => {
  if (props.operators && props.operators.length) return props.operators;
  return [];
});

const latexHtml = ref<string>("");
const latexError = ref<string>("");
const latexMode = ref<boolean>(false);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const mathFieldEl = ref<any | null>(null);
const modalEl = ref<HTMLElement | null>(null);

const mathLiveOptions = computed(() => props.mathLiveOptions ?? {});

const showLatexMode = computed(
  () => mathLiveOptions.value.showLatexMode ?? true
);

const showMenuToggle = computed(
  () => mathLiveOptions.value.showMenuToggle ?? true
);

const showVirtualKeyboard = computed(
  () => mathLiveOptions.value.showVirtualKeyboard ?? true
);

const backendShowVirtualKeyboard = computed(
  () => mathLiveOptions.value.showVirtualKeyboard ?? true
);

const canUseVirtualKeyboard = computed(() => {
  if (platform.value === "mobile") return backendShowVirtualKeyboard.value;
  return false;
});



const shouldUseVirtualKeyboard = computed(() => {
  return backendShowVirtualKeyboard.value;
});

const mathVirtualKeyboardPolicy = computed(() => {
  if (!backendShowVirtualKeyboard.value) return "sandboxed";

  return "manual";
});
const showVirtualKeyboardToggle = computed(() => {
  return backendShowVirtualKeyboard.value;
});

const hasAutoOpenedKeyboard = ref(false);

function showVirtualKeyboardOnceOnMobile() {
  if (platform.value !== "mobile") return;
  if (!backendShowVirtualKeyboard.value) return;
  if (hasAutoOpenedKeyboard.value) return;

  hasAutoOpenedKeyboard.value = true;

  setTimeout(() => {
    const vk = getVirtualKeyboard();

    prepareMathFieldForVirtualKeyboard();

    requestAnimationFrame(() => {
      try {
        vk?.show?.();
      } catch (error) {
        console.warn("Erro ao abrir teclado virtual:", error);
      }
    });
  }, 120);
}

const keyboardLayouts = computed(
  () =>
    mathLiveOptions.value.keyboardLayouts ?? [
      "alphabetic",
      "numeric",
      "symbols",
      "greek",
    ]
);

watch(
  showLatexMode,
  (enabled) => {
    if (!enabled) {
      latexMode.value = false;
    }
  },
  { immediate: true }
);

const keyboardHeight = ref(0);

function getVirtualKeyboard(): any | null {
  return (window as any).mathVirtualKeyboard ?? null;
}

function renderOp(latex: string): string {
  try {
    return katex.renderToString(latex, { throwOnError: false });
  } catch {
    return latex;
  }
}

function syncFromMathField() {
  if (mathFieldEl.value) {
    latexValue.value = mathFieldEl.value.value || "";
  }
}

function applyPageKeyboardInset(height: number) {
  document.documentElement.style.setProperty("--vk-height", `${height}px`);
  document.body.style.setProperty("--vk-height", `${height}px`);
}

function clearPageKeyboardInset() {
  document.documentElement.style.removeProperty("--vk-height");
  document.body.style.removeProperty("--vk-height");
}

let geometryChangeTimer: ReturnType<typeof setTimeout> | null = null;

function applyKeyboardGeometry() {
  const vk = getVirtualKeyboard();
  const height = vk?.boundingRect?.height || 0;

  keyboardHeight.value = height;
  applyPageKeyboardInset(height);

  if (platform.value === "mobile") {
    keyboardLift.value = 0;
    return;
  }

  nextTick(() => {
    updateModalLift();
  });
}

function handleGeometryChange() {
  if (geometryChangeTimer) {
    clearTimeout(geometryChangeTimer);
    geometryChangeTimer = null;
  }

  if (platform.value === "mobile") {
    geometryChangeTimer = setTimeout(() => {
      applyKeyboardGeometry();
      geometryChangeTimer = null;
    }, 120);

    return;
  }

  applyKeyboardGeometry();
}

const vkAttachedForSession = ref(false);

function attachVirtualKeyboard() {
  const vk = getVirtualKeyboard();
  if (!vk) return;

  vk.container = document.body;
  vk.alphabeticLayout = "qwerty";
  vk.layouts = keyboardLayouts.value;

  vk.removeEventListener?.("geometrychange", handleGeometryChange);

  if (shouldUseVirtualKeyboard.value) {
    vk.addEventListener?.("geometrychange", handleGeometryChange);
  } else {
    vk.hide?.();
    keyboardHeight.value = 0;
    keyboardLift.value = 0;
    clearPageKeyboardInset();
  }

  vkAttachedForSession.value = true;
}

const keyboardLift = ref(0);

function updateModalLift() {
  const el = modalEl.value;
  if (!el) {
    keyboardLift.value = 0;
    return;
  }

  const rect = el.getBoundingClientRect();
  const topGap = platform.value === "mobile" ? 8 : 20;
  const bottomGap = platform.value === "mobile" ? 8 : 20;

  const visibleBottom = window.innerHeight - keyboardHeight.value - bottomGap;


  const neededLift = Math.max(0, rect.bottom - visibleBottom);

  const maxLift = Math.max(0, rect.top - topGap);

  keyboardLift.value = Math.min(neededLift, maxLift);
}

function detachVirtualKeyboard() {
  const vk = getVirtualKeyboard();

  if (geometryChangeTimer) {
    clearTimeout(geometryChangeTimer);
    geometryChangeTimer = null;
  }

  if (vk) {
    vk.removeEventListener?.("geometrychange", handleGeometryChange);

    try {
      vk.hide?.();
    } catch (error) {
      console.warn("Erro ao fechar teclado virtual:", error);
    }
  }

  keyboardHeight.value = 0;
  keyboardLift.value = 0;
  clearPageKeyboardInset();
  vkAttachedForSession.value = false;
}

const boundingBox = computed(() => modalEl.value?.getBoundingClientRect());



function initMathField() {
  if (!mathFieldEl.value) return;

  const el = mathFieldEl.value;

  el.value = latexValue.value || "";
  el.mathVirtualKeyboardPolicy = mathVirtualKeyboardPolicy.value;

  requestAnimationFrame(() => {
    attachVirtualKeyboard();
    el.focus?.();

    showVirtualKeyboardOnceOnMobile();
  });
}

function updatePreview() {
  const val = latexValue.value;

  if (!latexMode.value) {
    latexHtml.value = "";
    latexError.value = "";
    return;
  }

  try {
    latexHtml.value = katex.renderToString(val, { throwOnError: false });
    latexError.value = "";
  } catch {
    latexHtml.value = val;
    latexError.value = "Erro ao renderizar";
  }
}

watch(
  () => latexValue.value,
  () => {
    updatePreview();
  },
  { immediate: true }
);

watch(
  () => latexMode.value,
  async (isLatex) => {
    updatePreview();

    if (!isLatex) {
      await nextTick();
      initMathField();
    } else {
      detachVirtualKeyboard();
      await nextTick();
      textareaRef.value?.focus();
    }
  }
);

watch(
  () => props.visible,
  async (v) => {
    if (v && !latexMode.value) {
      hasAutoOpenedKeyboard.value = false;

      await nextTick();
      initMathField();
    } else if (!v) {
      hasAutoOpenedKeyboard.value = false;
      detachVirtualKeyboard();
    }
  }
);

watch(
  () => props.modelValue,
  (val) => {
    if (!latexMode.value && mathFieldEl.value) {
      const el = mathFieldEl.value;
      if (el.value !== val) {
        el.value = val || "";
      }
    }
  }
);

function insertOperator(template: string) {
  if (!latexMode.value && mathFieldEl.value) {
    const el = mathFieldEl.value;

    if (typeof el.insert === "function") {
      el.insert(template, {
        selectionMode: "placeholder",
        focus: true,
        format: "latex",
      });
    } else if (typeof el.executeCommand === "function") {
      el.executeCommand([
        "insert",
        template,
        {
          selectionMode: "placeholder",
          focus: true,
          format: "latex",
        },
      ]);
    } else {
      el.value = (el.value || "") + template;
    }

    el.focus?.();
    syncFromMathField();
    return;
  }

  const ta = textareaRef.value;
  const current = latexValue.value || "";

  if (!ta) {
    latexValue.value = current + template;
    return;
  }

  const start = ta.selectionStart ?? current.length;
  const end = ta.selectionEnd ?? start;

  const before = current.slice(0, start);
  const after = current.slice(end);

  latexValue.value = before + template + after;

  nextTick(() => {
    const pos = start + template.length;
    ta.focus();
    ta.setSelectionRange(pos, pos);
  });
}

function handleModalKeydown(event: KeyboardEvent) {
  if (latexMode.value) return;
  if (platform.value === "mobile") return;
  if (event.key !== "Enter") return;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;

  event.preventDefault();
  event.stopPropagation();
  onConfirm();
}

function onClose() {
  detachVirtualKeyboard();
  emit("close");
}

function onConfirm() {
  detachVirtualKeyboard();
  emit("confirm", latexValue.value);
}

onBeforeUnmount(() => {
  detachVirtualKeyboard();
});

const isDocked = ref(false);

const savedDx = ref(0);
const savedDy = ref(0);

const DOCK_GRIP = 44;
const DOCK_MARGIN = 8;

const chevronRightSvg = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <polyline points="9 18 15 12 9 6"></polyline>
</svg>
`;

const chevronLeftSvg = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <polyline points="15 18 9 12 15 6"></polyline>
</svg>
`;

const dx = ref(0);
const dy = ref(0);

const modalStyle = computed(() => {
  const lift = platform.value === "mobile" ? 0 : keyboardLift.value;

  return {
    transform: `translate3d(${dx.value}px, calc(${dy.value}px - ${lift}px), 0)`,
    "--bounding-top": `${boundingBox.value?.top ?? 0}px`,
  };
});

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function getDockBounds() {
  if (props.teleportTarget instanceof HTMLElement) {
    const rect = props.teleportTarget.getBoundingClientRect();

    return {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  }

  return {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function toggleDock() {
  const el = modalEl.value;
  if (!el) return;

  if (!isDocked.value) {
    savedDx.value = dx.value;
    savedDy.value = dy.value;

    const rect = el.getBoundingClientRect();
    const bounds = getDockBounds();

    const targetLeft = bounds.right - DOCK_GRIP - DOCK_MARGIN;
    const targetTop = clamp(
      rect.top,
      bounds.top + DOCK_MARGIN,
      bounds.bottom - DOCK_GRIP - DOCK_MARGIN
    );

    dx.value += targetLeft - rect.left;
    dy.value += targetTop - rect.top;

    isDocked.value = true;
  } else {
    dx.value = savedDx.value;
    dy.value = savedDy.value;
    isDocked.value = false;
  }
}

function prepareMathFieldForVirtualKeyboard() {
  if (!backendShowVirtualKeyboard.value) return;
  if (!mathFieldEl.value) return;

  const el = mathFieldEl.value;

  el.mathVirtualKeyboardPolicy = "manual";

  // Re-attaching on every tap rebuilt the shared vk and could snap its layer back to alphabetic.
  if (!vkAttachedForSession.value) {
    attachVirtualKeyboard();
  }

  requestAnimationFrame(() => {
    el.focus?.();
  });
}
</script>

<style scoped>
.modal-backdrop {
  position: absolute;
  inset: 0;
  background: transparent;
  pointer-events: auto;
}

.modal-backdrop.is-local {
  position: absolute;
}

.modal {
  --modal-top: 12%;
  --modal-bottom-gap: 20px;

  position: absolute;
  top: var(--modal-top);
  right: 24px;
  background: #ffffff;
  padding: 24px 28px;
  border-radius: 18px;
  width: 40%;
  max-width: 40%;

  max-height: calc(100% - var(--modal-top) - var(--modal-bottom-gap));
  height: auto;

  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: visible;
  pointer-events: auto;
}

.modal.is-local {
  position: absolute;
}

.mobile .modal {
  --modal-top: 8px;
  --modal-bottom-gap: 8px;
  gap: 6px;

  right: 5%;
  width: 90%;
  max-width: 90%;

  max-height: calc(100dvh - var(--modal-top) - var(--vk-height, 0px) - var(--modal-bottom-gap));
}

.modal h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.mode-toggle {
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
}

.mode-toggle label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.mode-toggle input[type="checkbox"] {
  display: none;
}

.toggle-switch {
  width: 38px;
  height: 20px;
  border-radius: 999px;
  background: #e5e7eb;
  position: relative;
  transition: background 0.2s ease;
}

.toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.mode-toggle input[type="checkbox"]:checked+.toggle-switch {
  background: #00acdf;
}

.mode-toggle input[type="checkbox"]:checked+.toggle-switch .toggle-knob {
  transform: translateX(18px);
}

.field-label {
  color: #374151;
  font-size: 16px;
}

.mathlive-field {
  width: 100%;
  min-height: 72px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  font-size: 20px;
  box-sizing: border-box;
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
}

.operators {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}

.op-btn {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #d4d4d8;
  background: #ffffff;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  height: 32px;
  justify-content: center;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease,
    transform 0.05s ease;
}

.op-btn span {
  display: inline-block;
  line-height: 1.1;
}

.operators-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.op-btn:hover {
  border-color: #00acdf;
  box-shadow: 0 0 0 1px rgba(0, 172, 223, 0.2);
  background: #f0fbff;
}

.op-btn:active {
  transform: translateY(1px);
}

.latex-textarea {
  width: 100%;
  min-height: 90px;
  resize: vertical;
  margin-top: 4px;
  margin-bottom: 8px;
  font-family: monospace;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  box-sizing: border-box;
}

.preview-label {
  font-size: 13px;
  margin-bottom: 4px;
  color: #4b5563;
}

.latex-preview {
  min-height: 60px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}

.latex-error {
  color: #b91c1c;
  font-size: 12px;
  margin-top: 4px;
}

.modal-buttons {
  margin-top: 8px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.modal-buttons button {
  font-size: 14px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  min-width: 96px;
}

.btn-secondary {
  background: #ffffff;
  border-color: #d1d5db;
  color: #374151;
}

.btn-secondary:hover {
  background: #f3f4f6;
}

.btn-primary {
  background: #00acdf;
  border-color: #00acdf;
  color: #ffffff;
  font-weight: 500;
}

.btn-primary:hover {
  background: #0097c3;
  border-color: #0097c3;
}

.close-icon {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.operators .op-btn {
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
}

.operators .op-btn :deep(svg) {
  fill: currentColor !important;
  stroke: currentColor !important;
}

.operators .op-btn :deep(.katex),
.operators .op-btn :deep(.katex *) {
  color: currentColor !important;
}

.latex-preview,
.latex-preview * {
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
  opacity: 1 !important;
}

.dock-handle {
  position: absolute;
  left: -22px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px 0 0 14px;
  border: 1px solid #d1d5db;
  border-right: 0;
  background: #ffffff;
  cursor: pointer;
  z-index: 5;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.dock-handle:hover {
  border-color: #00acdf;
}

.modal.is-docked .dock-handle {
  left: -34px;
  height: 84px;
  width: 44px;
}

.dock-handle {
  transition: left 0.18s ease, height 0.18s ease, width 0.18s ease;
}

.mathlive-field.hide-menu-toggle::part(menu-toggle) {
  display: none !important;
}

.mathlive-field.hide-vk-toggle::part(virtual-keyboard-toggle) {
  display: none !important;
}
</style>